import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { DataGrid, ptBR } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useAuth } from "~/auth/AuthProvider";
import { listAdminCompanies } from "~/services/admin/companies";
import PageHeader from "~/shared/ui/PageHeader";
import { useNotify } from "~/shared/ui/feedback/SnackbarProvider";
import DeactivateCompanyDialog from "~/features/companies/DeactivateCompanyDialog";
import ReactivateCompanyDialog from "~/features/companies/ReactivateCompanyDialog";
import { isInactive } from "~/features/companies/types";
import { LoadErrorBanner } from "../shared/StatePanels";
import CompanyDialog from "./CompanyDialog";
import InactiveChip from "~/shared/ui/InactiveChip";

const managers = (row) => Number(row.managersCount) || 0; // camelCase explícito no BE (Task 15 do plano do BE)
// Busca sem acento e sem caixa ("épsilon" acha "Épsilon Med").
const fold = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
const CHIP = { height: 20, fontSize: 11 };

export default function CompaniesPage() {
  const notify = useNotify();
  // A rota já é só de Admin; ainda assim Desativar/Reativar só aparecem para o papel Admin (defesa extra; a API devolve 403).
  const { permissions } = useAuth();
  const canToggleActive = permissions.admin === true;
  const [state, setState] = useState({ status: "loading", rows: [] });
  const [attempt, setAttempt] = useState(0);
  // "Incluir inativas" começa desligado (CA6); não vai para a preferência.
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState({ open: false, company: null });
  const [menu, setMenu] = useState(null); // { anchor, company }
  const [toDeactivate, setToDeactivate] = useState(null);
  const [toReactivate, setToReactivate] = useState(null);

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, status: "loading" }));
    listAdminCompanies({ includeInactive }).then(
      (rows) => alive && setState({ status: "ok", rows }),
      () => alive && setState((s) => ({ ...s, status: "error" }))
    );
    return () => {
      alive = false;
    };
  }, [attempt, includeInactive]);

  const reload = () => setAttempt((n) => n + 1);
  // Os diálogos de desativar/reativar não fecham sozinhos: zera a empresa selecionada ANTES de recarregar,
  // para o diálogo fechar na hora (sem segundo clique disparando onDone de novo).
  const done = (message) => {
    setToDeactivate(null);
    setToReactivate(null);
    notify({ severity: "success", message });
    reload();
  };

  const term = fold(search.trim());
  const rows = term ? state.rows.filter((r) => fold(r.name).includes(term)) : state.rows;

  const columns = [
    {
      field: "name",
      headerName: "Empresa",
      flex: 1,
      minWidth: 260,
      sortable: true,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, color: isInactive(row) ? "text.secondary" : undefined }}>
          <Box component="span" sx={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis" }}>
            {row.name}
          </Box>
          <Typography variant="caption" color="text.secondary">
            #{row.id}
          </Typography>
          {row.legacy && (
            <Tooltip title="Cadastro antigo da integração Meu Plantão">
              <Chip size="small" variant="outlined" label="Legado" sx={CHIP} />
            </Tooltip>
          )}
        </Box>
      ),
    },
    { field: "cnpj", headerName: "CNPJ", width: 170, sortable: false },
    { field: "cep", headerName: "CEP", width: 100, sortable: false },
    {
      field: "managers",
      headerName: "Gestores",
      width: 100,
      sortable: true,
      type: "number",
      valueGetter: ({ row }) => managers(row),
      renderCell: ({ value }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {value === 0 && <WarningAmberIcon aria-label="Empresa sem gestor" sx={{ fontSize: 16, color: "#ef6c00" }} />}
          {value}
        </Box>
      ),
    },
    {
      // openPackagesCount vem pronto da listagem do Admin (pacotes fora de done/canceled/merged/hide).
      field: "openPackagesCount",
      headerName: "Pacotes abertos",
      width: 140,
      sortable: true,
      type: "number",
      valueGetter: ({ row }) => Number(row.openPackagesCount) || 0,
    },
    {
      field: "active",
      headerName: "Situação",
      width: 110,
      sortable: false,
      renderCell: ({ row }) =>
        isInactive(row) ? (
          <InactiveChip />
        ) : (
          <Chip size="small" label="Ativa" sx={{ ...CHIP, bgcolor: "#e6f4ea", color: "#1e6b34" }} />
        ),
    },
    {
      field: "actions",
      headerName: "Ações",
      width: 110,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => (
        <>
          <IconButton aria-label={`Editar ${row.name}`} onClick={() => setDialog({ open: true, company: row })}>
            <EditOutlinedIcon />
          </IconButton>
          <IconButton aria-label={`Mais ações de ${row.name}`} aria-haspopup="menu" onClick={(e) => setMenu({ anchor: e.currentTarget, company: row })}>
            <MoreVertIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Empresas"
        description="Empresas médicas atendidas pelo Antecipe. Os gestores de cada uma são definidos em Acessos."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, company: null })}>
            Nova empresa
          </Button>
        }
      />
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          placeholder="Buscar empresa"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputProps={{ "aria-label": "Buscar empresa" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: "1 1 220px", maxWidth: 360 }}
        />
        <FormControlLabel
          control={<Switch size="small" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />}
          label="Incluir inativas"
        />
      </Paper>
      {state.status === "error" ? (
        <LoadErrorBanner message="Não foi possível carregar as empresas. Tente novamente em instantes." onRetry={reload} />
      ) : (
        <Paper variant="outlined">
          <DataGrid
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            rows={rows}
            columns={columns}
            loading={state.status === "loading"}
            autoHeight
            disableVirtualization
            disableColumnMenu
            disableSelectionOnClick
            pageSize={50}
            rowsPerPageOptions={[50]}
            sx={{ border: 0, bgcolor: "background.paper" }}
          />
        </Paper>
      )}
      <Menu
        anchorEl={menu?.anchor}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {menu && (
          <MenuItem
            onClick={() => {
              setDialog({ open: true, company: menu.company });
              setMenu(null);
            }}
          >
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Editar empresa
          </MenuItem>
        )}
        {menu && canToggleActive && !isInactive(menu.company) && (
          <MenuItem
            onClick={() => {
              setToDeactivate(menu.company);
              setMenu(null);
            }}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <BlockIcon fontSize="small" color="error" />
            </ListItemIcon>
            Desativar empresa
          </MenuItem>
        )}
        {menu && canToggleActive && isInactive(menu.company) && (
          <MenuItem
            onClick={() => {
              setToReactivate(menu.company);
              setMenu(null);
            }}
          >
            <ListItemIcon>
              <RestartAltIcon fontSize="small" />
            </ListItemIcon>
            Reativar empresa
          </MenuItem>
        )}
      </Menu>
      <CompanyDialog
        open={dialog.open}
        company={dialog.company}
        onClose={() => setDialog({ open: false, company: null })}
        onSaved={(message) => {
          setDialog({ open: false, company: null });
          notify({ severity: "success", message });
          reload();
        }}
      />
      <DeactivateCompanyDialog company={toDeactivate} onClose={() => setToDeactivate(null)} onDone={done} />
      <ReactivateCompanyDialog company={toReactivate} onClose={() => setToReactivate(null)} onDone={done} />
    </Box>
  );
}
